import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router-dom'
import LoginForm from './LoginForm'
import RegisterForm from './RegisterForm'
import ForgotPasswordForm from './ForgotPasswordForm'

type AuthMode = 'login' | 'register' | 'forgot-password'

const AuthPage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const [mode, setMode] = useState<AuthMode>(
    location.pathname === '/register'
      ? 'register'
      : location.pathname.includes('forgot-password')
      ? 'forgot-password'
      : 'login'
  )

  useEffect(() => {
    if (location.pathname === '/register') {
      setMode('register')
    } else if (location.pathname === '/login') {
      setMode('login')
    } else if (location.pathname.includes('forgot-password')) {
      setMode('forgot-password')
    }
  }, [location.pathname])

  const handleToggleMode = () => {
    if (mode === 'login') {
      navigate('/register')
    } else {
      navigate('/login')
    }
  }

  const handleForgotPassword = () => {
    navigate('/forgot-password')
  }

  const handleBackToLogin = () => {
    navigate('/login')
  }

  const renderForm = () => {
    switch (mode) {
      case 'login':
        return (
          <LoginForm
            onToggleMode={handleToggleMode}
            onForgotPassword={handleForgotPassword}
          />
        )
      case 'register':
        return (
          <RegisterForm
            onToggleMode={handleToggleMode}
            onBackToLogin={handleBackToLogin}
          />
        )
      case 'forgot-password':
        return (
          <ForgotPasswordForm
            onBackToLogin={handleBackToLogin}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-terra-50 via-surface to-terra-50 dark:from-ink-900 dark:via-ink-700 dark:to-ink-900 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        {/* Left side - Branding */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="hidden lg:block"
        >
          <div className="text-center lg:text-left">
            <h1 className="text-4xl xl:text-5xl font-bold text-ink-900 dark:text-cream-50 mb-6">
              Transforme a criação de{' '}
              <span className="text-terra-500 dark:text-terra-300">avaliações educacionais</span>
            </h1>
            <p className="text-xl text-ink-500 dark:text-cream-300 mb-8 leading-relaxed">
              Plataforma completa para professores criarem, personalizarem e gerenciarem
              avaliações com banco de questões completo e geração de PDFs otimizados.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-moss-100 dark:bg-moss-500/30 dark:bg-moss-500/30 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-moss-500 dark:text-moss-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-ink-700 dark:text-cream-300">Banco com milhares de questões</span>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-moss-100 dark:bg-moss-500/30 dark:bg-moss-500/30 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-moss-500 dark:text-moss-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-ink-700 dark:text-cream-300">Geração de PDF profissional</span>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-moss-100 dark:bg-moss-500/30 dark:bg-moss-500/30 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-moss-500 dark:text-moss-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-ink-700 dark:text-cream-300">Templates personalizáveis</span>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-moss-100 dark:bg-moss-500/30 dark:bg-moss-500/30 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-moss-500 dark:text-moss-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-ink-700 dark:text-cream-300">Modelos prontos alinhados à BNCC</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right side - Auth Form */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="w-full"
        >
          <div className="bg-surface dark:bg-ink-700 rounded-caderno-md shadow-caderno-xl p-8 lg:p-10">
            {renderForm()}
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default AuthPage